import './ModuleList.scss';
import '../../Admin.scss';
import '../../../../styles/_table.scss';
import { Table, TableHead, TableRow, TableCell, TableBody, LoadingPlaceholder, IconButton } from '@trackman/web-shared-components';
import HeaderCreateButton from 'components/Admin/HeaderCreateButton/HeaderCreateButton';
import HeaderSearchbar from 'components/Admin/HeaderSearchbar/HeaderSearchbar';
import DeleteListItemModal from 'components/Admin/Modals/DeleteListItemModal/DeleteListItemModal';
import Paragraph from 'components/TextComps/Paragraph';
import { Link } from 'react-router-dom';
import { AdminModulesV2RelativeRoutes, AdminPagesRelativeRoutes } from 'Routes';
import useSetPageTitle from 'utilities/useSetPageTitle';
import { ReactComponent as PencilIcon } from 'images/icons/Pencil.svg';
import { ReactComponent as ArrowDownIcon } from 'images/icons/ArrowDown.svg';
import { ReactComponent as ArrowUpIcon } from 'images/icons/ArrowUp.svg';
import { ReactComponent as TrashIcon } from 'images/icons/Trash.svg';
import { TableStatusChip } from '@trackman/web-shared-components';
import { AdminModuleListEntry, AdminModulesSortPayload, TranslationStatus } from 'types/admin/modulesV2';
import { TranslationStatusChip } from '../TranslationStatusChip/TranslationStatusChip';
import SectionTitle from 'components/TextComps/SectionTitle';
import { useMemo, useState } from 'react';
import { useAdminModuleListV2 } from 'hooks/Admin/ModulesV2/useAdminModuleListV2';
import { useSortModulesV2 } from 'hooks/Admin/ModulesV2/useSortModulesV2';
import { useDeleteModuleV2 } from 'hooks/Admin/ModulesV2/useDeleteModuleV2';

export const ModuleList = () => {
  useSetPageTitle({ title: 'Learning Hub Admin' });

  const [filter, setFilter] = useState<string>('');
  const [isModalOn, setIsModalOn] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState<string | null>(null);

  const { data, isPending } = useAdminModuleListV2();
  const filteredModules: AdminModuleListEntry[] | undefined = useMemo(
    () => (!filter ? data : data?.filter((module) => module.title.toLowerCase().includes(filter.toLowerCase()))),
    [filter, data]
  );

  const deleteModule = useDeleteModuleV2();

  const handleDeleteClick = (id: string) => {
    setModuleToDelete(id);
    setIsModalOn(true);
  };

  const handleDeleteConfirm = () => {
    if (moduleToDelete) {
      deleteModule.mutate(moduleToDelete, {
        onSettled: () => {
          setModuleToDelete(null);
          setIsModalOn(false);
        },
      });
    }
  };

  return (
    <>
      <div className='default-page-header'>
        <h1 className='page-title'>Learning Hub Admin</h1>
        <span className='page-subtitle'>Administrate and manage the modules here</span>
      </div>
      <div className='admin-content-container'>
        {!isPending ? (
          filteredModules && filteredModules.length > 0 ? (
            <>
              <div className='admin-list-header'>
                <HeaderSearchbar searchValue={filter} onChange={(filter) => setFilter(filter)} placeholder='Search Modules' />
                <HeaderCreateButton
                  buttonTitle='Create Module'
                  route={`${AdminPagesRelativeRoutes.ModulesV2}/${AdminModulesV2RelativeRoutes.ModuleItem}`}
                />
              </div>

              <div className='tmu-table-wrapper'>
                <Table className='tmu-table admin-table'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Contents</TableCell>
                      <TableCell>Translation</TableCell>
                      <TableCell>Restricted Access</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredModules.map((row, index) => {
                      return (
                        <ModuleListRow
                          key={index}
                          id={row.id}
                          title={row.title}
                          numberOfCertifications={row.numberOfCertifications}
                          numberOfChapters={row.numberOfChapters}
                          translationStatus={row.translationStatus}
                          isRestricted={row.isOrganizationOnly}
                          status={row.status}
                          isFirst={index === 0}
                          isLast={filteredModules !== undefined && index === filteredModules.length - 1}
                          onDeleteClick={handleDeleteClick}
                        />
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : (
            <div className='empty-state-card'>
              <div>
                <SectionTitle>
                  <>No Modules added yet.</>
                </SectionTitle>
                <Paragraph>
                  <>No Modules have been added yet.</>
                </Paragraph>
                <Paragraph>
                  <>Create one to get started</>
                </Paragraph>
              </div>
              <HeaderCreateButton
                buttonTitle='Create Module'
                route={`${AdminPagesRelativeRoutes.ModulesV2}/${AdminModulesV2RelativeRoutes.ModuleItem}`}
                customClasses='secondary create-module-button'
              />
            </div>
          )
        ) : (
          <LoadingPlaceholder />
        )}
      </div>
      <DeleteListItemModal isModalOn={isModalOn} setIsModalOn={setIsModalOn} deleteAction={handleDeleteConfirm} itemName='module' />
    </>
  );
};

type ModuleListRowProps = {
  id: string;
  title: string;
  numberOfChapters: number;
  numberOfCertifications: number;
  translationStatus: TranslationStatus;
  isRestricted: boolean;
  status: string;
  isFirst: boolean;
  isLast: boolean;
  onDeleteClick: (id: string) => void;
};
const ModuleListRow = ({
  id,
  title,
  numberOfChapters,
  numberOfCertifications,
  translationStatus,
  isRestricted,
  status,
  isFirst,
  isLast,
  onDeleteClick,
}: ModuleListRowProps) => {
  const sortModules = useSortModulesV2();

  const statusLabel = useMemo(() => {
    switch (status) {
      case 'ComingSoon':
        return 'Coming Soon';
      case 'Hidden':
        return 'Hidden';
      case 'Visible':
        return 'Visible';
      default:
        return 'Unknown';
    }
  }, [status]);

  const statusIntent = useMemo(() => {
    switch (status) {
      case 'ComingSoon':
        return 'info';
      case 'Hidden':
        return 'grey';
      case 'Visible':
        return 'success';
      default:
        return 'grey';
    }
  }, [status]);

  const restrictedLabel = useMemo(() => {
    return isRestricted ? 'Yes' : 'No';
  }, [isRestricted]);

  return (
    <TableRow hover tabIndex={-1} key={id}>
      <TableCell>
        <div>{title}</div>
      </TableCell>
      <TableCell>
        <div className='contents-chapters'>{`${numberOfChapters} Chapter${numberOfChapters > 1 ? 's' : ''}`}</div>
        <div className='contents-certifications'>{`${numberOfCertifications} Certification${numberOfCertifications > 1 ? 's' : ''}`}</div>
      </TableCell>
      <TableCell>
        <TranslationStatusChip status={translationStatus} />
      </TableCell>
      <TableCell>
        <div>{restrictedLabel}</div>
      </TableCell>
      <TableCell>
        <TableStatusChip label={statusLabel} intent={statusIntent}></TableStatusChip>
      </TableCell>
      <TableCell>
        <div className='action-buttons'>
          <Link to={`/Admin/${AdminPagesRelativeRoutes.ModulesV2}/${AdminModulesV2RelativeRoutes.ModuleItem}/${id}`} className='row-button'>
            <PencilIcon />
          </Link>
          <IconButton
            icon={() => <ArrowUpIcon />}
            style={isFirst ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
            onClick={() => (!isFirst ? sortModules.mutate({ moduleId: id, moveUp: true } as AdminModulesSortPayload) : {})}
          />
          <IconButton
            icon={() => <ArrowDownIcon />}
            style={isLast ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
            onClick={() => (!isLast ? sortModules.mutate({ moduleId: id, moveUp: false } as AdminModulesSortPayload) : {})}
          />
          <button className='row-button' onClick={() => onDeleteClick(id)}>
            <TrashIcon />
          </button>
        </div>
      </TableCell>
    </TableRow>
  );
};
